import { yupResolver } from '@hookform/resolvers/yup';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { DataCollectionEmailPreferences } from '../../../data-models/preferences-v3.data-model';
import {
  DataCollectionTemplateDefaultMessages,
  fetchDataCollectionTemplateDefaults,
} from '../../../services/queries/KPIRequestsQueries';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { ConfigStatus } from '../DataDisplay/useDisplayConfigState';
import { dataCollectionEmailConfigSchema } from './dataCollectionEmailConfig.schema';
import {
  buildEffectiveConfig,
  getDefaultFormConfig,
  useDataCollectionConfigActions,
} from './useDataCollectionConfigActions';

export function useDataCollectionConfigFormState() {
  const [editStatus, setEditStatus] = useState<ConfigStatus>('initial');
  const [templateDefaults, setTemplateDefaults] = useState<DataCollectionTemplateDefaultMessages | null>(
    null
  );
  const { config, saveConfig, resetConfig } = useDataCollectionConfigActions();
  const schema = dataCollectionEmailConfigSchema();
  const methods = useForm<DataCollectionEmailPreferences>({
    resolver: yupResolver(schema as ObjectSchema<DataCollectionEmailPreferences>),
    defaultValues: buildEffectiveConfig(config, null),
    mode: 'all',
  });

  const {
    getValues,
    formState: { errors, isDirty },
    reset,
    trigger,
  } = methods;

  useEffect(() => {
    let cancelled = false;
    fetchDataCollectionTemplateDefaults()
      .then((defaults) => {
        if (cancelled) {
          return;
        }
        setTemplateDefaults(defaults);
        // Pre-fill unset fields with the text the backend will actually send,
        // unless the admin already started editing.
        if (!methods.formState.isDirty) {
          methods.reset(buildEffectiveConfig(config, defaults));
        }
      })
      .catch((e) => Sentry.captureException(e));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDirty && editStatus === 'initial') {
      setEditStatus('modified');
    }
  }, [isDirty, editStatus]);

  const onSave = useCallback(async () => {
    const isValid = await trigger();
    if (!isValid) {
      captureFormValidationError('Data Collection Email Config', errors);
      return;
    }
    await saveConfig(getValues());
    setEditStatus('saved');
  }, [trigger, saveConfig, getValues, errors]);

  const onReset = useCallback(async () => {
    await resetConfig();
    reset(buildEffectiveConfig(getDefaultFormConfig(), templateDefaults));
    setEditStatus('initial');
  }, [reset, resetConfig, templateDefaults]);

  return { editStatus, onSave, onReset, methods };
}
